/**
 * Cooling cooked food, with the timers doing the remembering (audit COVERAGE-CREATION gap 4, 2026-09-30).
 *
 * The moment: a cook at 2 pm, a stockpot of chili off the stove, the rush over, prepping for dinner. One tap starts the
 * batch; the app reminds them at 1:45, 2:00, 5:45 and 6:00; every reading lands on the cooling log with who and when.
 *
 * The rule, FDA Food Code 2022 (adopted in Utah by R392-100), text checked against the FDA PDF (January 18, 2023 version):
 *   3-501.14 Cooling. "(A) Cooked time/temperature control for safety food shall be cooled:
 *     (1) Within 2 hours from 57ºC (135ºF) to 21ºC (70°F); and
 *     (2) Within a total of 6 hours from 57ºC (135ºF) to 5ºC (41°F) or less."
 *   "(B) TIME/TEMPERATURE CONTROL FOR SAFETY FOOD shall be cooled within 4 hours to 5oC (41oF) or less if prepared from
 *     ingredients at ambient temperature, such as reconstituted FOODS and canned tuna."
 * 3-501.14(B) is that 4-hour rule, NOT the reheat rule. What to do when cooling misses a stage is FDA guidance, Food
 * Code 2022 Annex 5, 7 "Assessing Cooling":
 *   "Cooked hot food may be reheated to 165 ºF for 15 seconds and the cooling process started again using a different
 *    cooling method if the food is: Above 70 ºF and two hours or less into the cooling process; and Above 41 ºF and six
 *    hours or less into the cooling process. Cooked hot food should be discarded immediately if the food is: Above 70 ºF
 *    and more than two hours into the cooling process; or Above 41 ºF and more than six hours into the cooling process."
 * The source protocols (docs/source/parish-food-safety-protocol.md, "Cooling hot food") are the stricter of the two for
 * stage 2 and are what this app follows: stage 1 missed, "Reheat to 165°F and start over, or discard"; stage 2 missed,
 * "Discard". Corrective actions list: "reheat to 165°F and restart cooling if within the first 2 hours; otherwise discard."
 *
 * Gates (packages/core/test/cooling.test.ts):
 * | C1 Start at 135 | A start reading above 135°F doesn't start the clock (it starts at 135); 41°F or below needs no cooling |
 * | C2 Two deadlines | 70°F by start + 2 h, 41°F by start + 6 h, from the start tap (server time) |
 * | C3 Truthful fix | Over 70°F at or before 2:00: reheat to 165°F and start over, or throw it out. After 2:00: throw it out. Over 41°F at 6:00 or later: throw it out |
 * | C4 Not yet is not a failure | Over the limit before its deadline: keep cooling, nothing is written as failed |
 * | C5 Timers remember | Notices at 1:45, 2:00, 5:45 and 6:00, each saying the one next step |
 * | C6 Schedule by kind | Restaurants and community kitchens cool; coffee shops and trucks only when the menu cooks ahead; homes never |
 */
import type { IsoDateTime, LogEntry, OrgKind, PermitType } from "./model.ts";
import { MENU_ITEMS } from "./playbooks.ts";

export const COOLING = {
  /** 3-501.14(A): the clock runs from 57ºC (135ºF). */
  startMaxF: 135,
  /** 3-501.14(A)(1): 21ºC (70°F) within 2 hours. */
  stage1F: 70,
  stage1Min: 120,
  /** 3-501.14(A)(2): 5ºC (41°F) or less within a total of 6 hours. */
  stage2F: 41,
  totalMin: 360,
  /** The heads-up before each deadline (the owner's brief: 1:45 before 2:00). A judgment default, not a legal number. */
  warnMin: 15,
  /** Annex 5 7: reheat "to 165 ºF for 15 seconds" before starting over. */
  reheatF: 165,
} as const;

export const COOLING_CITE = "Food Code 3-501.14(A)";
export const COOLING_FIX_CITE = "Food Code 2022 Annex 5, 7 Assessing Cooling; source protocols: cooling hot food (if missed)";
export const COOLING_METHODS_CITE = "Food Code 3-501.15";

/** One plain "why", quoted from the Food Code's own public health reason (Annex 3, 3-501.14). No statistic. */
export const COOLING_WHY = {
  line: "Cooling too slowly is one of the leading causes of foodborne illness.",
  quote: "Excessive time for cooling of time/temperature control for safety foods has been consistently identified as one of the leading contributing factors to foodborne illness.",
  cite: "Food Code 2022 Annex 3, 3-501.14",
};

export interface CoolingMethod { id: "ice_bath" | "shallow_pans" | "ice_wand" | "blast_chiller"; title: string; how: string; cite: string }
/** 3-501.15(A): shallow pans; smaller or thinner portions; rapid cooling equipment; stirring in an ice water bath; adding ice. */
export const COOLING_METHODS: CoolingMethod[] = [
  { id: "ice_bath", title: "Ice-water bath", how: "Set the pot in ice water up to the food level. Stir every 15 to 30 minutes.", cite: "Food Code 3-501.15(A)(4); source protocols: how to cool fast" },
  // The 2-inch depth is the source protocols' number; the Food Code itself says "shallow pans" with no depth.
  { id: "shallow_pans", title: "Shallow pans", how: "Split it into metal pans 2 inches deep or less. Leave them uncovered on the top shelf until 41°F.", cite: "Food Code 3-501.15(A)(1), (B)(2); source protocols: shallow pans 2 inches deep or less" },
  { id: "ice_wand", title: "Ice wand", how: "Stir with a frozen ice paddle, or add ice in place of some of the water.", cite: "Food Code 3-501.15(A)(4), (6); source protocols: ice wands" },
  { id: "blast_chiller", title: "Blast chiller", how: "If you have one, it cools fastest. A normal fridge holds cold; it doesn't cool a big pot fast.", cite: "Food Code 3-501.15(A)(3); Annex 3, 3-501.15" },
];

/* ─────────────── which places cool (C6) ─────────────── */

/**
 * Does this place cook food and cool it for later? Then the cooling log is one of its base checks and "Start cooling"
 * is always one tap away. Restaurants and community (church) kitchens cook ahead; a coffee shop or food truck does
 * only when its menu has a TCS item that is cooled or reheated (made ahead). A home never (no permit, docs/HOME.md).
 */
export function coolsFood(p: { kind?: OrgKind; permit?: PermitType; menuItemIds?: readonly string[] }): boolean {
  if (p.kind === "home") return false;
  if (p.kind === "restaurant" || p.kind === "community_space") return true;
  const ids = new Set(p.menuItemIds ?? []);
  return MENU_ITEMS.some((m) => ids.has(m.id) && m.tcs && m.steps.some((s) => s.step === "cool" || s.step === "reheat"));
}

/* ─────────────── starting (C1) ─────────────── */

/** Why a start reading can't start the clock, in plain words; undefined when it can. */
export function coolingStartProblem(t0: number): string | undefined {
  if (!Number.isFinite(t0)) return "Type the temperature you read.";
  if (t0 > COOLING.startMaxF) return `Still above ${COOLING.startMaxF}°F. The 2-hour clock starts at ${COOLING.startMaxF}°F: start cooling it now and read it again in a few minutes.`;
  if (t0 <= COOLING.stage2F) return `${t0}°F is already ${COOLING.stage2F}°F or below. It's cold: label it and put it in the fridge.`;
  return undefined;
}

/* ─────────────── a batch, from its cooling-log record ─────────────── */

export type CoolingStatus =
  /** Waiting for 70°F by the 2-hour mark. */
  | "stage1"
  /** 70°F reached; waiting for 41°F by the 6-hour mark. */
  | "stage2"
  /** 41°F reached in time: into cold holding. */
  | "done"
  /** A reading missed its limit and nothing is written yet about what was done. */
  | "fix"
  /** Closed with what was done (reheated to start over, or thrown out). */
  | "closed";

export type CoolingRecord = Pick<LogEntry, "id" | "values" | "createdAt"> & Partial<Pick<LogEntry, "createdBy" | "correctiveAction" | "cellSources" | "correction">>;

export interface CoolingBatch {
  id: string;
  food: string;
  startedAt: IsoDateTime;
  startedBy: string;
  t0?: number;
  t2?: number;
  t2At?: IsoDateTime;
  t6?: number;
  t6At?: IsoDateTime;
  /** 70°F deadline (start + 2 h) and 41°F deadline (start + 6 h). */
  stage1By: IsoDateTime;
  stage2By: IsoDateTime;
  status: CoolingStatus;
  /** Which reading failed, when status is "fix". */
  failed?: "2h" | "6h";
  action?: string;
}

const addMin = (iso: IsoDateTime, m: number) => new Date(Date.parse(iso) + m * 60_000).toISOString();
const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v)) ? Number(v) : undefined);

/** A cooling-log record as a batch. The clock starts when the record was made (the Start cooling tap). */
export function coolingBatch(r: CoolingRecord): CoolingBatch {
  const t0 = num(r.values.t0), t2 = num(r.values.t2), t6 = num(r.values.t6);
  const action = r.correctiveAction ?? (typeof r.values.action === "string" && r.values.action.trim() ? r.values.action : undefined);
  const stage1By = addMin(r.createdAt, COOLING.stage1Min), stage2By = addMin(r.createdAt, COOLING.totalMin);
  const fail2 = t2 !== undefined && t2 > COOLING.stage1F, fail6 = t6 !== undefined && t6 > COOLING.stage2F;
  const status: CoolingStatus = fail2 || fail6 ? (action ? "closed" : "fix") : t6 !== undefined ? "done" : action ? "closed" : t2 !== undefined ? "stage2" : "stage1";
  return {
    id: r.id, food: String(r.values.food ?? "Food").trim() || "Food", startedAt: r.createdAt, startedBy: r.createdBy ?? "",
    ...(t0 !== undefined ? { t0 } : {}), ...(t2 !== undefined ? { t2, ...(r.cellSources?.t2 ? { t2At: r.cellSources.t2.at } : {}) } : {}),
    ...(t6 !== undefined ? { t6, ...(r.cellSources?.t6 ? { t6At: r.cellSources.t6.at } : {}) } : {}),
    stage1By, stage2By, status, ...(status === "fix" ? { failed: fail2 ? "2h" as const : "6h" as const } : {}), ...(action ? { action } : {}),
  };
}

/** Still being cooled (a timer is running or a fix is owed). A record marked "not done after all" never counts. */
export const isOpenBatch = (b: CoolingBatch) => b.status === "stage1" || b.status === "stage2" || b.status === "fix";
export const openCoolingBatches = (rows: readonly CoolingRecord[]) => rows.filter((r) => !r.correction).map(coolingBatch).filter(isOpenBatch);

/* ─────────────── a reading (C3, C4) ─────────────── */

export type CoolingVerdict =
  /** Within the limit: saved to the log. */
  | { kind: "ok"; stage: "2h" | "6h"; column: "t2" | "t6"; line: string }
  /** Over the limit, but the deadline hasn't come: keep cooling. Not written as a failure (C4). */
  | { kind: "not_yet"; stage: "2h" | "6h"; line: string; minutesLeft: number }
  /** Over the limit at or past the deadline: saved to the log as out of range, and what to do. */
  | { kind: "missed"; stage: "2h" | "6h"; column: "t2" | "t6"; line: string; options: ("reheat" | "discard")[] };

const clockLeft = (m: number) => (m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`);

/**
 * What a reading means, `at` minutes after the start. The stage is the next one owed: 70°F until it's reached, then 41°F.
 * Stage 1 over 70°F: at or before 2:00 the fix is "reheat to 165°F and start over, or throw it out"; after 2:00 only
 * "throw it out" (Annex 5, 7). Stage 2 over 41°F at or after 6:00: throw it out (source protocols).
 */
export function coolingVerdict(b: Pick<CoolingBatch, "t2" | "food">, temp: number, minutesIn: number): CoolingVerdict {
  const m = Math.max(0, Math.floor(minutesIn));
  if (b.t2 === undefined) {
    if (temp <= COOLING.stage1F) return { kind: "ok", stage: "2h", column: "t2", line: m <= COOLING.stage1Min ? `${temp}°F: stage 1 done. Now to ${COOLING.stage2F}°F by the 6-hour mark.` : `${temp}°F, read ${clockLeft(m - COOLING.stage1Min)} after the 2-hour mark. Now to ${COOLING.stage2F}°F by the 6-hour mark.` };
    if (m < COOLING.stage1Min) return { kind: "not_yet", stage: "2h", line: `${temp}°F: not at ${COOLING.stage1F}°F yet. Keep cooling. ${clockLeft(COOLING.stage1Min - m)} left.`, minutesLeft: COOLING.stage1Min - m };
    if (m === COOLING.stage1Min) return { kind: "missed", stage: "2h", column: "t2", line: `${temp}°F at 2 hours: it didn't reach ${COOLING.stage1F}°F. Reheat it to ${COOLING.reheatF}°F and start over with a faster way, or throw it out.`, options: ["reheat", "discard"] };
    return { kind: "missed", stage: "2h", column: "t2", line: `${temp}°F, more than 2 hours in: it didn't reach ${COOLING.stage1F}°F in time. Throw it out.`, options: ["discard"] };
  }
  if (temp <= COOLING.stage2F) return { kind: "ok", stage: "6h", column: "t6", line: `${temp}°F: cooled. Cover it, date it and keep it at ${COOLING.stage2F}°F or below.` };
  if (m < COOLING.totalMin) return { kind: "not_yet", stage: "6h", line: `${temp}°F: not at ${COOLING.stage2F}°F yet. Keep cooling. ${clockLeft(COOLING.totalMin - m)} left.`, minutesLeft: COOLING.totalMin - m };
  return { kind: "missed", stage: "6h", column: "t6", line: `${temp}°F at 6 hours: it didn't reach ${COOLING.stage2F}°F. Throw it out.`, options: ["discard"] };
}

/** Before 2:00 you may still choose to reheat and start over (Annex 5, 7: "two hours or less into the cooling process"). */
export const canReheat = (minutesIn: number, b: Pick<CoolingBatch, "t2">) => b.t2 === undefined ? minutesIn <= COOLING.stage1Min : false;

/** The corrective-action words written on the log for each choice (the paper form's "Corrective action" cell). */
export const COOLING_FIX_TEXT: Record<"reheat" | "discard", string> = {
  reheat: `Reheated to ${COOLING.reheatF}°F to start cooling over with a faster way.`,
  discard: "Thrown out.",
};

/* ─────────────── the timers (C5) ─────────────── */

export interface CoolingNotice { key: string; at: IsoDateTime; title: string; body: string; stage: "2h" | "6h" }

/**
 * The four notices for a batch, each with the next step. Keys are stable (one notice, one key), so a sender that
 * dedupes on key never sends one twice: the phone schedules them locally today, a push sender can later (the seam).
 */
export function coolingNotices(b: Pick<CoolingBatch, "id" | "food" | "startedAt" | "t2" | "status">): CoolingNotice[] {
  const f = b.food;
  const at = (m: number) => addMin(b.startedAt, m);
  const out: CoolingNotice[] = [];
  if (b.status === "stage1") {
    out.push({ key: `cool:${b.id}:1h45`, at: at(COOLING.stage1Min - COOLING.warnMin), stage: "2h", title: `Check ${f}: 70°F by the 2-hour mark`, body: `15 minutes left. It must be ${COOLING.stage1F}°F or below at the 2-hour mark. Still hot? Stir it, or split it into shallow pans.` });
    out.push({ key: `cool:${b.id}:2h`, at: at(COOLING.stage1Min), stage: "2h", title: `2 hours: read ${f} now`, body: `It must be ${COOLING.stage1F}°F or below. Tap to log the reading.` });
  }
  if (b.status === "stage1" || b.status === "stage2") {
    out.push({ key: `cool:${b.id}:5h45`, at: at(COOLING.totalMin - COOLING.warnMin), stage: "6h", title: `Check ${f}: 41°F by the 6-hour mark`, body: `15 minutes left. It must be ${COOLING.stage2F}°F or below at the 6-hour mark.` });
    out.push({ key: `cool:${b.id}:6h`, at: at(COOLING.totalMin), stage: "6h", title: `6 hours: read ${f} now`, body: `It must be ${COOLING.stage2F}°F or below. Tap to log the reading.` });
  }
  return out;
}

/* ─────────────── the checks it adds to the day (schedule.ts) ─────────────── */

export interface CoolingCheck {
  key: string;
  label: string;
  hint: string;
  dueAt: IsoDateTime;
  lateAt: IsoDateTime;
  cooling: { entryId: string; stage: "2h" | "6h"; food: string };
}

/**
 * The expected checks a batch adds: the 2-hour reading until it's taken (or the batch is closed), then the 6-hour one.
 * Due at the deadline itself, with no grace: the deadline is the law's, not a habit.
 */
export function coolingChecks(b: CoolingBatch, timeLabel: (iso: IsoDateTime) => string = (iso) => iso.slice(11, 16)): CoolingCheck[] {
  const out: CoolingCheck[] = [];
  const c = (stage: "2h" | "6h", dueAt: IsoDateTime, label: string, hint: string) => out.push({ key: `cool-${stage}:${b.id}`, label, hint, dueAt, lateAt: dueAt, cooling: { entryId: b.id, stage, food: b.food } });
  // The 2-hour check stays listed (done) once taken, so the day shows it; the 6-hour one only once stage 1 is met.
  c("2h", b.stage1By, `${b.food}: 70°F by ${timeLabel(b.stage1By)}`, `Cooling since ${timeLabel(b.startedAt)}. ${COOLING.stage1F}°F or below within 2 hours (${COOLING_CITE}(1)).`);
  if (b.t2 !== undefined && b.t2 <= COOLING.stage1F)
    c("6h", b.stage2By, `${b.food}: 41°F by ${timeLabel(b.stage2By)}`, `${COOLING.stage2F}°F or below within 6 hours in all (${COOLING_CITE}(2)).`);
  return out;
}

/** Has this batch's record answered the check? The 2-hour one by a 70°F reading or a written fix; the 6-hour one by its reading. */
export function coolingCheckDone(stage: "2h" | "6h", r: CoolingRecord): boolean {
  const b = coolingBatch(r);
  return stage === "2h" ? b.t2 !== undefined || b.status === "closed" : b.t6 !== undefined || b.status === "closed";
}

/* ─────────────── what the apps show (API and mocks alike) ─────────────── */

export interface CoolingView extends CoolingBatch {
  /** Whole minutes since the start, at `now`. */
  minutesIn: number;
  /** The reading owed next, or null when the batch is finished. */
  owed: "2h" | "6h" | null;
  /** Reheating to start over is still allowed now (up to 2:00, stage 1 only). */
  canReheat: boolean;
  /** The timers still to come (the phone schedules them locally). */
  notices: CoolingNotice[];
}

export function coolingView(b: CoolingBatch, now: IsoDateTime): CoolingView {
  const minutesIn = Math.max(0, Math.floor((Date.parse(now) - Date.parse(b.startedAt)) / 60_000));
  const owed = b.status === "stage1" ? "2h" : b.status === "stage2" ? "6h" : b.status === "fix" ? b.failed ?? null : null;
  // A missed 2-hour reading may be reheated when the READING was taken at or before 2:00 (Annex 5, 7), whenever the tap.
  const readAt = b.t2At ? Math.floor((Date.parse(b.t2At) - Date.parse(b.startedAt)) / 60_000) : minutesIn;
  return { ...b, minutesIn, owed, canReheat: b.status === "stage1" ? canReheat(minutesIn, b) : b.status === "fix" && b.failed === "2h" && readAt <= COOLING.stage1Min, notices: coolingNotices(b).filter((n) => n.at > now) };
}

/** The records a day times: open batches (any day) and ones started since `dayStart`. Not-done-after-all ones never. */
export function coolingForDay<R extends CoolingRecord>(rows: readonly R[], dayStart: IsoDateTime): R[] {
  return rows.filter((r) => !r.correction && (isOpenBatch(coolingBatch(r)) || r.createdAt >= dayStart));
}

/* ─────────────── one step on a batch: the same decision for the API and both demo mocks ─────────────── */

export type CoolingOp = { kind: "reading"; temp: number } | { kind: "fix"; choice: "reheat" | "discard"; temp?: number; note?: string };
export type CoolingStep =
  | { error: { status: 400 | 409; message: string } }
  /** `changes`: cells to write (none for "not yet"); `corrective`: the fix words for the log. */
  | { verdict?: CoolingVerdict; changes: Record<string, string>; corrective?: string };

export function coolingStep(r: CoolingRecord, op: CoolingOp, now: IsoDateTime): CoolingStep {
  const b = coolingBatch(r);
  if (b.status === "done" || b.status === "closed") return { error: { status: 409, message: "This batch is finished. Start a new one." } };
  const view = coolingView(b, now);
  const bad = (t: unknown) => typeof t !== "number" || !Number.isFinite(t) || t < -50 || t > 500;
  if (op.kind === "reading") {
    if (bad(op.temp)) return { error: { status: 400, message: "Type the temperature you read." } };
    if (b.status === "fix") return { error: { status: 400, message: "Say what was done about the last reading first." } };
    const verdict = coolingVerdict(b, op.temp, view.minutesIn);
    return { verdict, changes: verdict.kind === "not_yet" ? {} : { [verdict.column]: String(op.temp) } };
  }
  if (op.choice === "reheat" && !view.canReheat) return { error: { status: 400, message: `More than 2 hours in and over ${COOLING.stage1F}°F: it can't be reheated to start over. Throw it out.` } };
  const changes: Record<string, string> = {};
  if (op.temp !== undefined) {
    if (bad(op.temp)) return { error: { status: 400, message: "Type the temperature you read." } };
    if (b.status === "stage1") changes.t2 = String(op.temp);
    else if (b.status === "stage2") changes.t6 = String(op.temp);
  } else if (op.choice === "reheat" && b.status === "stage1") return { error: { status: 400, message: "Type the reading that says it needs reheating." } };
  const note = op.note?.trim().slice(0, 300);
  return { changes, corrective: `${COOLING_FIX_TEXT[op.choice]}${note ? ` ${note}` : ""}` };
}

/**
 * The in-app alert to show now (web, and the phone while open): the latest of the batch's four notices that has come,
 * for the reading still owed. Undefined before 1:45 and once the reading is in. Same words as the phone's notices.
 */
export function coolingAlertNow(b: Pick<CoolingBatch, "id" | "food" | "startedAt" | "t2" | "status">, now: IsoDateTime): CoolingNotice | undefined {
  const owed = b.status === "stage1" ? "2h" : b.status === "stage2" ? "6h" : undefined;
  if (!owed) return undefined;
  return coolingNotices(b).filter((n) => n.stage === owed && n.at <= now).pop();
}

/**
 * The batch page's reminder line, from the batch's own notice times (persona review 2026-10-01 A10): "Reminders at
 * 4:47 PM, 5:02 PM, 8:47 PM and 9:02 PM". It said "Reminders at 1:45, 2:00, 5:45 and 6:00", elapsed hours that read as
 * clock times beside "70°F by 5:02 PM". Only the reminders still to come; undefined when none are left.
 */
export function coolingReminderLine(v: Pick<CoolingView, "notices">, timeLabel: (iso: IsoDateTime) => string): string | undefined {
  const at = v.notices.map((n) => timeLabel(n.at));
  if (!at.length) return undefined;
  return `${at.length === 1 ? "Reminder at" : "Reminders at"} ${at.length === 1 ? at[0] : `${at.slice(0, -1).join(", ")} and ${at.at(-1)}`}`;
}

/** "1 h 12 min left" / "5 min left" / "Was due 4:00 PM" for the reading owed. */
export function coolingLeftLine(v: Pick<CoolingView, "owed" | "stage1By" | "stage2By">, now: IsoDateTime, timeLabel: (iso: IsoDateTime) => string): string {
  if (!v.owed) return "";
  const by = v.owed === "2h" ? v.stage1By : v.stage2By;
  const m = Math.ceil((Date.parse(by) - Date.parse(now)) / 60_000);
  const limit = v.owed === "2h" ? COOLING.stage1F : COOLING.stage2F;
  if (m <= 0) return `${limit}°F was due ${timeLabel(by)}. Read it now.`;
  return `${limit}°F by ${timeLabel(by)} · ${m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`} left`;
}
